import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, X } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface AccessibleModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /**
   * If true, clicking the backdrop does NOT close the modal (destructive actions).
   * Escape key still works.
   */
  preventBackdropClose?: boolean
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

/**
 * F3.A11Y-1 + F3.A11Y-2: Reusable accessible dialog wrapper built on Radix UI.
 * Provides:
 * - Focus trap (focus stays inside the dialog when tabbing)
 * - Escape key to close
 * - ARIA roles (dialog, dialog-title, dialog-description)
 * - Initial focus on first focusable element
 * - Restoration of focus to previous element on close
 */
export default function AccessibleModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  preventBackdropClose = false,
}: AccessibleModalProps) {
  const { t } = useTranslation()
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={preventBackdropClose ? undefined : () => onOpenChange(false)}
        />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-[100] w-[95vw] ${sizeClasses[size]} -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-card border border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95`}
          onEscapeKeyDown={() => onOpenChange(false)}
          onPointerDownOutside={(e) => {
            if (preventBackdropClose) e.preventDefault();
          }}
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="text-sm text-muted-foreground mt-1">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors flex-shrink-0"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'default'
  onConfirm: () => void
  icon?: ReactNode
}

const variantClasses = {
  danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  warning: 'bg-amber-500 text-white hover:bg-amber-500/90',
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
}

const variantIconClasses = {
  danger: 'bg-destructive/10 text-destructive',
  warning: 'bg-amber-500/10 text-amber-500',
  default: 'bg-primary/10 text-primary',
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  onConfirm,
  icon,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  return (
    <AccessibleModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      preventBackdropClose={variant === 'danger'}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`p-2 rounded-lg flex-shrink-0 ${variantIconClasses[variant]}`}>
          {icon || <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1" />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-secondary transition-colors"
        >
          {cancelLabel || t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => {
            onConfirm();
            onOpenChange(false);
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${variantClasses[variant]}`}
        >
          {confirmLabel || t('common.confirm')}
        </button>
      </div>
    </AccessibleModal>
  );
}
